import { Course, CourseStatus } from "@/services/courses/types/course.types";
import { syncPdcMemberWithRetries } from "@/services/pdc";
import { ensurePdcEmbedApiKey, PDC_EMBED_STORAGE_KEY } from "@/utils/pdcEmbed";
import { getPdcApiBaseUrl } from "@/utils/pdc-external-course-list";
import { isAxiosError } from "axios";
import { pdcExternalApi } from "./client";

/** Shape returned by sg-pdc-api for GET /external/courses (includes optional PDC completion stats). */
export type PdcExternalCourseListItem = {
  id: number;
  title: string;
  thumbImage?: string | null;
  progress?: number;
  started?: boolean;
  /** Present when course_stats.completed_at is set (same weighting as /external/reality/points). */
  examScore?: number;
  examPoints?: number;
  completionPoints?: number;
  totalPoints?: number;
};

function resolveThumbnailUrl(thumb: string | null | undefined, apiBase: string): string {
  const t = thumb?.trim();
  if (!t) return "/assets/icons/quiz.svg";
  if (/^https?:\/\//i.test(t)) return t;
  const path = t.startsWith("/") ? t : `/${t}`;
  return `${apiBase}${path}`;
}

export function mapPdcListItemToCourse(item: PdcExternalCourseListItem, apiBase: string): Course {
  const progress = Math.min(100, Math.max(0, Number(item.progress) || 0));
  const started = Boolean(item.started);

  let status: CourseStatus = CourseStatus.PENDING;
  if (progress >= 100) {
    status = CourseStatus.FINISHED;
  } else if (started || progress > 0) {
    status = CourseStatus.IN_PROGRESS;
  }

  return {
    title: item.title,
    thumbnailUrl: resolveThumbnailUrl(item.thumbImage, apiBase),
    courseUrl: `courses/${item.id}`,
    examPoints: undefined,
    coursePoints: undefined,
    status,
    points: 0,
    pdcCourseId: item.id,
  };
}

export function derivePdcCourseListStats(courses: Course[]): {
  coursesWithProgressCount: number;
  totalCoursesCount: number;
  completedCoursesCount: number;
} {
  const totalCoursesCount = courses.length;
  const completedCoursesCount = courses.filter((c) => c.status === CourseStatus.FINISHED).length;
  const coursesWithProgressCount = courses.filter(
    (c) => c.status === CourseStatus.IN_PROGRESS || c.status === CourseStatus.FINISHED
  ).length;
  return { coursesWithProgressCount, totalCoursesCount, completedCoursesCount };
}

async function getPdcExternalCoursesPayload(): Promise<unknown> {
  const res = await pdcExternalApi.get<unknown>("/external/courses");
  return res.data;
}

function isMemberNotFoundForExternalIdError(err: unknown): boolean {
  if (!isAxiosError(err) || err.response?.status !== 404) {
    return false;
  }
  const message = err.response?.data?.message;
  return (
    typeof message === "string" &&
    message.toLowerCase().includes("member not found")
  );
}

/**
 * @param userId Reality `users.id` (PDC `member.external_id`). Required so `_csgb` is always
 * aligned before each request and after a post-sync retry.
 */
export async function fetchPdcExternalCourseList(userId: number | string): Promise<Course[]> {
  const apiBase = getPdcApiBaseUrl();
  if (!apiBase) {
    throw new Error("VITE_PDC_API_URL is not set");
  }

  const ensureKey = () => {
    ensurePdcEmbedApiKey(userId);
    const key =
      typeof localStorage === "undefined" ? null : localStorage.getItem(PDC_EMBED_STORAGE_KEY);
    if (!key?.trim()) {
      throw new Error("PDC x-api-key missing: localStorage _csgb");
    }
  };

  ensureKey();

  let raw: unknown;
  try {
    raw = await getPdcExternalCoursesPayload();
  } catch (err) {
    if (isMemberNotFoundForExternalIdError(err)) {
      const syncResult = await syncPdcMemberWithRetries();
      if (!syncResult.synced) {
        throw err;
      }

      ensureKey();
      raw = await getPdcExternalCoursesPayload();
    } else {
      throw err;
    }
  }

  if (!Array.isArray(raw)) {
    throw new TypeError("Invalid PDC courses response");
  }

  return (raw as PdcExternalCourseListItem[]).map((row) => mapPdcListItemToCourse(row, apiBase));
}
